import { useState } from "react";
import axios from "axios";
import { Loader2, KeyRound, Mail, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function PasswordInput({ testid, value, onChange, placeholder, minLength }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        required
        minLength={minLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-editorial pr-11"
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={() => setShow(!show)}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#C5A059] transition-colors"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function AdminLogin({ onLogin, resetToken = null }) {
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setSuccess("Si un compte existe avec cet email, un lien de réinitialisation (valable 72 h) vient d'être envoyé.");
      } else if (mode === "reset") {
        if (password !== password2) {
          setError("Les deux mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        await axios.post(`${API}/auth/reset-password`, { token: resetToken, password });
        setSuccess("Mot de passe réinitialisé ! Vous pouvez vous connecter.");
        setMode("login");
        setPassword("");
        setPassword2("");
        window.history.replaceState({}, "", "/admin");
      } else {
        const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
        if (data.token) {
          localStorage.setItem("d2r_token", data.token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        }
        onLogin(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY" className="h-16 w-auto mx-auto mb-10" />
        <h1 className="font-display text-3xl uppercase font-bold text-center text-white">
          Espace <span className="text-[#C5A059]">Admin</span>
        </h1>
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-10 bg-[#121214] border border-[#27272A] p-8 space-y-8">
          {mode === "login" && (
            <>
              <input data-testid="admin-login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-editorial" />
              <PasswordInput testid="admin-login-password" placeholder="Mot de passe" value={password} onChange={setPassword} />
            </>
          )}
          {mode === "forgot" && (
            <>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">Entrez votre email : vous recevrez un lien pour choisir un nouveau mot de passe (valable 72 h).</p>
              <input data-testid="admin-forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-editorial" />
            </>
          )}
          {mode === "reset" && (
            <>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">Choisissez votre nouveau mot de passe (min. 6 caractères).</p>
              <PasswordInput testid="admin-reset-password" minLength={6} placeholder="Nouveau mot de passe" value={password} onChange={setPassword} />
              <PasswordInput testid="admin-reset-password2" minLength={6} placeholder="Confirmez le mot de passe" value={password2} onChange={setPassword2} />
            </>
          )}
          {error && <p data-testid="admin-login-error" className="text-red-400 text-sm">{error}</p>}
          {success && <p data-testid="admin-login-success" className="text-emerald-400 text-sm">{success}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#C5A059] text-black py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : mode === "forgot" ? <Mail size={15} /> : mode === "reset" ? <KeyRound size={15} /> : null}
            {mode === "login" ? "Connexion" : mode === "forgot" ? "Recevoir le lien" : "Réinitialiser"}
          </button>
          <div className="flex justify-between text-xs">
            {mode !== "login" ? (
              <button type="button" onClick={() => { setMode("login"); setError(""); setSuccess(""); }} data-testid="admin-back-login" className="text-[#71717A] hover:text-[#C5A059] transition-colors">
                ← Retour à la connexion
              </button>
            ) : <span />}
            {mode === "login" && (
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="admin-forgot-link" className="text-[#A1A1AA] hover:text-[#C5A059] transition-colors">
                Mot de passe oublié ?
              </button>
            )}
          </div>
        </form>
        <a href="/" data-testid="admin-back-to-site" className="block text-center text-[#71717A] text-xs mt-6 hover:text-[#C5A059] transition-colors">
          ← Retour au site
        </a>
      </div>
    </div>
  );
}
